import { sessionManager } from "@/broadcastChannel/sessionManager";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type RemoteAutosaveStatus =
  | "disabled"
  | "syncing"
  | "synced"
  | "error";

type UseSynthesesRemoteAutosaveOptions<T> = {
  view: "financeurs" | "intervenants" | "projets";
  storageKey: string;
  draft: T;
  lastLocalSavedAt: string | null;
  debounceMs?: number;
  onRemoteDraftLoaded?: (payload: {
    draft: T;
    remoteUpdatedAt: string | null;
    remoteLastLocalSavedAt: string | null;
  }) => void;
};

function readRemoteEndpoint() {
  const explicitEndpoint = String(
    import.meta.env.VITE_SYNTHESES_AUTOSAVE_ENDPOINT || "",
  ).trim();
  if (explicitEndpoint) return explicitEndpoint;

  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/autosave`;
}

function readRemoteResetEndpoint(endpoint: string) {
  const explicitResetEndpoint = String(
    import.meta.env.VITE_SYNTHESES_AUTOSAVE_RESET_ENDPOINT || "",
  ).trim();
  if (explicitResetEndpoint) return explicitResetEndpoint;

  const normalizedEndpoint = String(endpoint || "").replace(/\/+$/, "");
  if (!normalizedEndpoint) return "";
  return `${normalizedEndpoint}/reset`;
}

function useSynthesesRemoteAutosave<T>({
  view,
  storageKey,
  draft,
  lastLocalSavedAt,
  debounceMs = 1200,
  onRemoteDraftLoaded,
}: UseSynthesesRemoteAutosaveOptions<T>) {
  const endpoint = useMemo(() => readRemoteEndpoint(), []);
  const resetEndpoint = useMemo(
    () => readRemoteResetEndpoint(endpoint),
    [endpoint],
  );
  const enabled = endpoint.length > 0;
  const [status, setStatus] = useState<RemoteAutosaveStatus>(
    enabled ? "synced" : "disabled",
  );
  const [initialLoadDone, setInitialLoadDone] = useState(!enabled);
  const [initialLoading, setInitialLoading] = useState(enabled);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const lastSyncedLocalSavedAtRef = useRef<string | null>(null);
  const syncingRef = useRef(false);

  const sendNow = useCallback(async () => {
    if (!enabled || !endpoint || !lastLocalSavedAt) return;
    if (syncingRef.current) return;
    if (lastSyncedLocalSavedAtRef.current === lastLocalSavedAt) return;

    syncingRef.current = true;
    setStatus("syncing");
    setErrorMessage(null);

    try {
      const token = sessionManager.getToken();
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          source: "syntheses-conventions",
          view,
          storageKey,
          draft,
          lastLocalSavedAt,
          clientDate: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      lastSyncedLocalSavedAtRef.current = lastLocalSavedAt;
      const syncedAt = new Date().toISOString();
      setLastSyncedAt(syncedAt);
      setStatus("synced");
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(String(error?.message || "remote-sync-error"));
    } finally {
      syncingRef.current = false;
    }
  }, [draft, enabled, endpoint, lastLocalSavedAt, storageKey, view]);

  const retry = useCallback(() => {
    setRetryNonce((prev) => prev + 1);
  }, []);

  const resetRemoteDraft = useCallback(async () => {
    if (!enabled || !resetEndpoint) return true;
    if (syncingRef.current) return false;

    syncingRef.current = true;
    setStatus("syncing");
    setErrorMessage(null);

    try {
      const token = sessionManager.getToken();
      const response = await fetch(resetEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          source: "syntheses-conventions",
          view,
          storageKey,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      lastSyncedLocalSavedAtRef.current = null;
      setStatus("synced");
      setLastSyncedAt(new Date().toISOString());
      return true;
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(String(error?.message || "remote-reset-error"));
      return false;
    } finally {
      syncingRef.current = false;
    }
  }, [enabled, resetEndpoint, storageKey, view]);

  useEffect(() => {
    if (!enabled) {
      setInitialLoading(false);
      setInitialLoadDone(true);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const loadRemoteDraft = async () => {
      setInitialLoading(true);

      try {
        const token = sessionManager.getToken();
        const search = new URLSearchParams({
          source: "syntheses-conventions",
          view,
          storageKey,
        });

        const response = await fetch(`${endpoint}?${search.toString()}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json();
        const remoteUpdatedAt =
          typeof payload?.updatedAt === "string" ? payload.updatedAt : null;
        const remoteLastLocalSavedAt =
          typeof payload?.lastLocalSavedAt === "string"
            ? payload.lastLocalSavedAt
            : null;

        if (
          payload?.found === true &&
          payload?.draft &&
          typeof payload.draft === "object"
        ) {
          if (remoteLastLocalSavedAt) {
            lastSyncedLocalSavedAtRef.current = remoteLastLocalSavedAt;
          }
          if (remoteUpdatedAt) {
            setLastSyncedAt(remoteUpdatedAt);
          }
          onRemoteDraftLoaded?.({
            draft: payload.draft as T,
            remoteUpdatedAt,
            remoteLastLocalSavedAt,
          });
        }

        if (!cancelled) {
          setStatus("synced");
          setErrorMessage(null);
        }
      } catch (error: any) {
        if (cancelled || error?.name === "AbortError") return;
        setStatus("error");
        setErrorMessage(String(error?.message || "remote-load-error"));
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
          setInitialLoadDone(true);
        }
      }
    };

    void loadRemoteDraft();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, endpoint, onRemoteDraftLoaded, retryNonce, storageKey, view]);

  useEffect(() => {
    if (!enabled) {
      setStatus("disabled");
      return;
    }
    if (!initialLoadDone) return;
    if (!lastLocalSavedAt) return;
    if (lastSyncedLocalSavedAtRef.current === lastLocalSavedAt) return;

    const timer = window.setTimeout(() => {
      void sendNow();
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    debounceMs,
    enabled,
    initialLoadDone,
    lastLocalSavedAt,
    sendNow,
    retryNonce,
  ]);

  return {
    endpointConfigured: enabled,
    initialLoadDone,
    initialLoading,
    status,
    lastSyncedAt,
    errorMessage,
    retry,
    resetRemoteDraft,
  };
}

export default useSynthesesRemoteAutosave;
